/**
 * Flickity carousel initialization and lifecycle management hook
 * Handles Flickity instance creation, resize handling, and portal rendering for carousel controls
 */

import Flickity from 'flickity'
import { ComponentChildren } from 'preact'
import { createPortal } from 'preact/compat'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import debounce from 'lodash.debounce'
import { applyHorizontalDragAxis } from '@/components/helpers/flickity-drag-axis'

// Vertical swipes must scroll the page, not drag the carousel.
applyHorizontalDragAxis()

const defaultOptions: Flickity.Options = {
  contain: true,
  resize: false
}

const resizeListener = (flickity: Flickity) => {
  const minHeightElements = flickity.slider.querySelectorAll('.min-h-full')
  minHeightElements.forEach((element: Element) => {
    element.classList.remove('min-h-full')
  })
  flickity.resize()
  flickity.reloadCells()
  minHeightElements.forEach((element: Element) => {
    element.classList.add('min-h-full')
  })
}

/**
 * Flickity promotes its whole `.flickity-slider` track to a compositor layer, not
 * just the visible window, so the instance is only kept alive near the viewport —
 * the same gate `x-carousel` applies, since Preact carousels (recommendations, GWP)
 * go through this hook instead. Two thresholds so parking on the boundary cannot thrash.
 */
const INIT_MARGIN = '600px'
const TEARDOWN_MARGIN = '1500px'

export function useFlickity(target: Element, options: Flickity.Options, deps: any[] = []) {
  const flickityRef = useRef<Flickity | null>(null)
  const sliderRef = useRef<Element | null>(null)
  const [isReady, setIsReady] = useState(false)
  // Defaults to true so the no-IntersectionObserver path, and the window before the
  // observers first fire, still initialise exactly as before — fail open.
  const [inViewRange, setInViewRange] = useState(true)
  const flickityOptions = { ...defaultOptions, ...options }

  useEffect(() => {
    if (!target || !('IntersectionObserver' in window)) return

    const initObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInViewRange(true) },
      { rootMargin: INIT_MARGIN }
    )
    const teardownObserver = new IntersectionObserver(
      ([entry]) => { if (!entry.isIntersecting) setInViewRange(false) },
      { rootMargin: TEARDOWN_MARGIN }
    )
    initObserver.observe(target)
    teardownObserver.observe(target)

    return () => {
      initObserver.disconnect()
      teardownObserver.disconnect()
    }
  }, [target])

  useEffect(() => {
    // Out of range: skip construction. The cleanup from the previous run has already
    // destroyed any existing instance, which is what frees the layer.
    if (!target || !inViewRange) return

    setIsReady(false)
    let fallbackTimer: NodeJS.Timeout | null = null
    let initFrame: number | null = null
    let cancelled = false
    const handleResize = debounce(() => {
      if (flickityRef.current) {
        resizeListener(flickityRef.current)
      }
    }, 100)

    initFrame = requestAnimationFrame(() => {
      initFrame = null
      // The effect can re-run before this frame lands (deps include `results`,
      // which changes as soon as the API responds). Without this guard the
      // already-torn-down run still builds a carousel and attaches a resize
      // listener that its own cleanup has no way to remove.
      if (cancelled) return

      const computedHeight = target.getBoundingClientRect().height
      if (computedHeight > 0) {
        (target as HTMLElement).style.minHeight = `${computedHeight}px`
      }

      flickityRef.current = new Flickity(target, { ...flickityOptions })

      let readyFired = false
      const handleReady = () => {
        if (readyFired) return
        readyFired = true
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if ((target as HTMLElement).style.minHeight) {
              (target as HTMLElement).style.minHeight = ''
            }
            setIsReady(true)
          })
        })
      }

      flickityRef.current.on('ready', handleReady)
      fallbackTimer = setTimeout(handleReady, 100)
      window.addEventListener('resize', handleResize)
      sliderRef.current = target.querySelector('.flickity-slider')
    })

    return () => {
      cancelled = true
      if (initFrame !== null) cancelAnimationFrame(initFrame)
      if (fallbackTimer) clearTimeout(fallbackTimer)

      // A queued resize would otherwise fire against a destroyed instance. On
      // mobile this matters: showing/hiding the URL bar while scrolling emits
      // `resize`, so these run constantly.
      handleResize.cancel()

      window.removeEventListener('resize', handleResize)
      flickityRef.current?.destroy()
      flickityRef.current = null
      setIsReady(false)
    }
    // `target` MUST be a dependency: it arrives via a ref callback, so the first run
    // bails at the guard above, and without it in the deps the effect never re-runs
    // once the node exists — the carousel silently never initialises.
  }, [target, inViewRange, ...deps])

  const SliderPortal = useCallback(
    ({ children }: {children: ComponentChildren}) => {
      if (!target) return children
      return createPortal(children, target)
    },
    [target]
  )

  return {
    flickity: flickityRef.current,
    SliderPortal,
    isReady,
    inViewRange
  }
}
